import { ProjectOverviewActions, ProjectOverviewActionTypes } from '../../actions/project-overview.actions';
import { ProjectOverview } from '../../../../shared/models/project-overview';

export interface ProjectOverviewSystemState {
  id: number;
  systemNumber: string;
  checkSheetCount: number;
  status: 'pending' | 'loading' | 'loaded' | 'error';
  errorMessage?: string | null;
  loadedComplete?: boolean;
}

export interface ProjectOverviewState {
  loading: boolean;
  data: ProjectOverview[] | null; // aggregated overview items across all systems
  latestError: string | null;
  systems: ProjectOverviewSystemState[];
  totalCheckSheets: number;
  loadedCheckSheets: number;
  loaded: boolean; // true when all relevant systems have finished loading
}

export const initialState: ProjectOverviewState = {
  loading: false,
  data: null,
  latestError: null,
  systems: [],
  totalCheckSheets: 0,
  loadedCheckSheets: 0,
  loaded: false,
};

export function projectOverviewReducer(state: ProjectOverviewState = initialState, action: ProjectOverviewActions): ProjectOverviewState {
  switch (action.type) {
    case ProjectOverviewActionTypes.Load:
      // Legacy single-call loading path (kept for backwards-compat)
      return { ...state, loading: true, latestError: null, loaded: false };

    case ProjectOverviewActionTypes.LoadSuccess:
      return { ...state, loading: false, data: action.payload as any, loaded: true };

    case ProjectOverviewActionTypes.LoadFailure:
      return { ...state, loading: false, latestError: action.errorMessage, loaded: true };

    case ProjectOverviewActionTypes.ClearError:
      return { ...state, latestError: null };

    // --- New system-based loading cases ---
    case ProjectOverviewActionTypes.LoadSystems:
      return {
        ...state,
        loading: true,
        latestError: null,
        systems: [],
        totalCheckSheets: 0,
        loadedCheckSheets: 0,
        data: null,
        loaded: false,
      };

    case ProjectOverviewActionTypes.LoadSystemsSuccess: {
      const systems: ProjectOverviewSystemState[] =
        (action as any).payload?.map((s: any) => ({
          id: s.id,
          systemNumber: s.systemNumber,
          checkSheetCount: s.checkSheetCount,
          status: s.checkSheetCount > 0 ? 'pending' : 'loaded', // systems without checksheets treated as already satisfied
          errorMessage: null,
        })) || [];

      // Only systems that actually have checksheets should contribute to the total
      const totalCheckSheets = systems.filter((s) => (s.checkSheetCount || 0) > 0).reduce((acc, s) => acc + (s.checkSheetCount || 0), 0);

      // If there are no checksheets at all, we consider overview loaded immediately.
      const loaded = totalCheckSheets === 0;

      return {
        ...state,
        loading: !loaded,
        systems,
        totalCheckSheets,
        loadedCheckSheets: 0,
        latestError: null,
        loaded,
      };
    }

    case ProjectOverviewActionTypes.LoadSystemsFailure:
      return {
        ...state,
        loading: false,
        latestError: (action as any).errorMessage,
        loaded: true,
      };

    case ProjectOverviewActionTypes.LoadSystemOverview: {
      const systemId = (action as any).system.id;
      const systems: ProjectOverviewSystemState[] = state.systems.map((s) =>
        s.id === systemId ? { ...s, status: 'loading', errorMessage: null } : s,
      );
      return { ...state, systems };
    }

    case ProjectOverviewActionTypes.LoadSystemOverviewSuccess: {
      const { systemId, payload } = action as any;
      const systems: ProjectOverviewSystemState[] = state.systems.map((s) =>
        s.id === systemId ? { ...s, status: 'loaded', errorMessage: null } : s,
      );
      const newData = [...(state.data || []), ...(payload || [])] as ProjectOverview[];
      const loadedCheckSheets = systems
        .filter((s) => s.status === 'loaded' && (s.checkSheetCount || 0) > 0)
        .reduce((acc, s) => acc + (s.checkSheetCount || 0), 0);
      const loaded = loadedCheckSheets >= state.totalCheckSheets;
      return {
        ...state,
        systems,
        data: newData,
        loadedCheckSheets,
        loaded,
        loading: !loaded,
      };
    }

    case ProjectOverviewActionTypes.LoadSystemOverviewFailure: {
      const { systemId, errorMessage } = action as any;
      const systems: ProjectOverviewSystemState[] = state.systems.map((s) =>
        s.id === systemId ? { ...s, status: 'error', errorMessage } : s,
      );
      return { ...state, systems, latestError: errorMessage, loading: false, loaded: true };
    }

    default:
      return state;
  }
}
